import React, { useState } from "react";
import KuikaTreeSelect, { ITreeSelectNode } from "../../../../ui-kit/kuika-tree-select/kuika-tree-select";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

const TREE_DATA: ITreeSelectNode[] = [
  {
    id: "engineering",
    name: "Engineering",
    path: "engineering",
    type: "folder",
    children: [
      { id: "fe", name: "Frontend", path: "engineering/frontend", type: "leaf" },
      { id: "be", name: "Backend", path: "engineering/backend", type: "leaf" },
      { id: "qa", name: "QA", path: "engineering/qa", type: "leaf" }
    ]
  },
  {
    id: "design",
    name: "Design",
    path: "design",
    type: "folder",
    children: [
      { id: "ui", name: "UI Designers", path: "design/ui", type: "leaf" },
      { id: "ux", name: "UX Researchers", path: "design/ux", type: "leaf" },
      { id: "brand", name: "Brand", path: "design/brand", type: "leaf", disabled: true }
    ]
  },
  { id: "ops", name: "Operations", path: "ops", type: "leaf" }
];

// Shaped like the Select Data action tree the floating mode was built for: a node can be picked
// exactly when it has no children, every folder is disabled (it opens, it is not a value), and a
// value is the action at the top level and a dotted path below it.
const ACTION_TREE: ITreeSelectNode[] = [
  { id: "GetCustomers", name: "GetCustomers", path: "GetCustomers", type: "leaf" },
  {
    id: "GetOrder",
    name: "GetOrder",
    path: "GetOrder",
    type: "folder",
    disabled: true,
    children: [
      {
        id: "GetOrder.customer",
        name: "customer",
        path: "GetOrder.customer",
        type: "folder",
        disabled: true,
        children: [
          { id: "GetOrder.customer.addresses", name: "addresses", path: "GetOrder.customer.addresses", type: "leaf" },
          { id: "GetOrder.customer.phones", name: "phones", path: "GetOrder.customer.phones", type: "leaf" }
        ]
      },
      { id: "GetOrder.lines", name: "lines", path: "GetOrder.lines", type: "leaf" }
    ]
  },
  { id: "GetOrderHistory", name: "GetOrderHistory", path: "GetOrderHistory", type: "leaf" },
  {
    id: "GetProduct",
    name: "GetProduct",
    path: "GetProduct",
    type: "folder",
    disabled: true,
    children: [
      { id: "GetProduct.variants", name: "variants", path: "GetProduct.variants", type: "leaf" },
      { id: "GetProduct.tags", name: "tags", path: "GetProduct.tags", type: "leaf" }
    ]
  },
  { id: "ListInvoices", name: "ListInvoices", path: "ListInvoices", type: "leaf" }
];

interface IFloatingStatefulProps {
  initial?: string;
  size?: "sm" | "md" | "lg";
  showLine?: boolean;
  disabled?: boolean;
}

const FloatingStateful: React.FC<IFloatingStatefulProps> = ({ initial, showLine = true, ...rest }) => {
  const [value, setValue] = useState<string | undefined>(initial);
  return (
    <KuikaTreeSelect
      multiple={false}
      popup="floating"
      treeData={ACTION_TREE}
      value={value}
      onChange={(next: string | undefined) => setValue(next)}
      placeholder="Please select"
      searchable
      allowClear
      showLine={showLine}
      {...rest}
    />
  );
};

interface IStatefulProps {
  showCheckbox?: boolean;
  showTags?: boolean;
  maxHeight?: number;
  disabled?: boolean;
  initial?: string[];
}

const Stateful: React.FC<IStatefulProps> = ({ initial = [], ...rest }) => {
  const [value, setValue] = useState<string[]>(initial);
  return (
    <KuikaTreeSelect
      treeData={TREE_DATA}
      value={value}
      onChange={(values: string[]) => setValue(values)}
      placeholder="Choose departments"
      {...rest}
    />
  );
};

export const TreeSelectStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Tree Select"
      componentName="KuikaTreeSelect"
      sourcePath="ui-kit/kuika-tree-select"
      description='Hierarchical picker. The inline mode multi-selects with checkboxes, folders collapse independently and parent state is computed; popup="floating" picks one path from a searchable tree in a list portaled over the page.'
    >
      <SpecRow
        label="Default"
        caption="checkboxes on, tag chips on — Tab to focus, Enter/Space/↓ opens, Esc closes"
        align="start"
      >
        <div style={{ width: 320 }}>
          <Stateful initial={["engineering/frontend"]} showCheckbox showTags />
        </div>
      </SpecRow>
      <SpecRow label="Without tag chips" caption="showTags=false" align="start">
        <div style={{ width: 320 }}>
          <Stateful initial={["design/ui", "design/ux"]} showCheckbox showTags={false} />
        </div>
      </SpecRow>
      <SpecRow label="Without checkboxes" caption="click-to-toggle" align="start">
        <div style={{ width: 320 }}>
          <Stateful showCheckbox={false} showTags />
        </div>
      </SpecRow>
      <SpecRow label="maxHeight" caption="dropdown scrolls at 180px" align="start">
        <div style={{ width: 320 }}>
          <Stateful maxHeight={180} />
        </div>
      </SpecRow>
      <SpecRow label="Disabled" align="start">
        <div style={{ width: 320 }}>
          <Stateful disabled initial={["engineering/frontend"]} />
        </div>
      </SpecRow>
      <SpecRow
        label="Floating, single pick"
        caption='popup="floating" multiple={false} — type to filter (name or path); ↓ lands on the first match that can be picked, Enter picks it; a disabled folder opens on a click'
        align="start"
      >
        <div style={{ width: 320 }}>
          <FloatingStateful />
        </div>
      </SpecRow>
      <SpecRow label="Floating, nested value" caption="the field shows the node's label, not its path" align="start">
        <div style={{ width: 320 }}>
          <FloatingStateful initial="GetOrder.customer.phones" />
        </div>
      </SpecRow>
      <SpecRow label="Floating, sizes" caption="sm · md · lg — KuikaSelectbox's field" align="start">
        <div className="kuika-flex-column--gap-s" style={{ width: 320 }}>
          <FloatingStateful size="sm" initial="GetCustomers" />
          <FloatingStateful size="md" initial="GetCustomers" />
          <FloatingStateful size="lg" initial="GetCustomers" />
        </div>
      </SpecRow>
      <SpecRow label="Floating, no guide lines" caption="showLine=false" align="start">
        <div style={{ width: 320 }}>
          <FloatingStateful showLine={false} />
        </div>
      </SpecRow>
      <SpecRow label="Floating, disabled" align="start">
        <div style={{ width: 320 }}>
          <FloatingStateful disabled initial="GetOrder.lines" />
        </div>
      </SpecRow>
    </ShowcaseFrame>
  );
};
